// src/services/OrganizationFeatureService.js

import { supabase } from '../lib/supabase';

export class OrganizationFeatureService {
  /**
   * Get all features for an organization
   * @param {string} organizationId - Organization UUID
   * @returns {Promise<Object>} Organization features
   */
  static async getOrganizationFeatures(organizationId) {
    const { data, error } = await supabase
      .from('organization_features')
      .select('*')
      .eq('organization_id', organizationId)
      .single();

    if (error) {
      console.error('Error fetching organization features:', error);
      throw error;
    }
    
    return data;
  }

  /**
   * Update organization features
   * @param {string} organizationId - Organization UUID
   * @param {Object} features - Features to update
   * @returns {Promise<Object>} Updated features
   */
  static async updateOrganizationFeatures(organizationId, features) {
    const { data, error } = await supabase
      .from('organization_features')
      .update(features)
      .eq('organization_id', organizationId)
      .select()
      .single();

    if (error) {
      console.error('Error updating organization features:', error);
      throw error;
    }
    
    return data;
  }

  /**
   * Check if a specific feature is enabled
   * @param {string} organizationId - Organization UUID
   * @param {string} featureName - Feature key to check
   * @returns {Promise<boolean>} Whether feature is enabled
   */
  static async isFeatureEnabled(organizationId, featureName) {
    try {
      const features = await this.getOrganizationFeatures(organizationId);
      return features?.[featureName] === true;
    } catch (error) {
      console.error('Error checking feature status:', error);
      return true; // Default to enabled on error
    }
  }

  /**
   * Get enabled features as an array
   * @param {string} organizationId - Organization UUID
   * @returns {Promise<string[]>} Array of enabled feature keys
   */
  static async getEnabledFeatures(organizationId) {
    try {
      const features = await this.getOrganizationFeatures(organizationId);
      return Object.keys(features).filter(key => 
        features[key] === true && 
        !['id', 'organization_id', 'created_at', 'updated_at'].includes(key)
      );
    } catch (error) {
      console.error('Error getting enabled features:', error);
      return [];
    }
  }

  /**
   * Bulk toggle multiple features
   * @param {string} organizationId - Organization UUID
   * @param {Object} featureToggles - Object with feature keys and boolean values
   * @returns {Promise<Object>} Updated features
   */
  static async toggleFeatures(organizationId, featureToggles) {
    return this.updateOrganizationFeatures(organizationId, featureToggles);
  }

  /**
   * Get feature categories with their features
   * @returns {Object} Feature categories and definitions
   */
  static getFeatureCategories() {
    return {
      management: {
        name: 'Management',
        icon: '👥',
        description: 'Core user and location management features',
        features: [
          { 
            key: 'student_management', 
            label: 'Student Management', 
            description: 'Manage student profiles, enrollments, and records',
            routes: ['/app/students']
          },
          { 
            key: 'parent_management', 
            label: 'Parent Management', 
            description: 'Manage parent accounts and family relationships',
            routes: ['/app/parents']
          },
          { 
            key: 'coach_management', 
            label: 'Coach Management', 
            description: 'Manage instructors and coach assignments',
            routes: ['/app/coaches']
          },
          { 
            key: 'center_management', 
            label: 'Center Management', 
            description: 'Manage multiple training locations',
            routes: ['/app/centers', '/app/managers']
          },
        ]
      },
      academic: {
        name: 'Academic & Training',
        icon: '🥋',
        description: 'Training, curriculum, and progress tracking',
        features: [
          { 
            key: 'class_management', 
            label: 'Class Management', 
            description: 'Schedule and manage classes',
            routes: ['/app/classes', '/app/upcoming-classes']
          },
          { 
            key: 'syllabus_management', 
            label: 'Syllabus Management', 
            description: 'Manage curriculum and training materials',
            routes: ['/app/syllabus']
          },
          { 
            key: 'belt_progression', 
            label: 'Belt Progression', 
            description: 'Track student belt levels and promotions',
            routes: ['/app/belt-levels']
          },
          { 
            key: 'attendance_system', 
            label: 'Attendance System', 
            description: 'Mark and track student attendance',
            routes: ['/app/mark-attendance', '/app/attendance-report']
          },
          { 
            key: 'student_progress', 
            label: 'Student Progress', 
            description: 'Track training hours and achievements',
            routes: ['/app/student-progress']
          },
        ]
      },
      financial: {
        name: 'Financial',
        icon: '💰',
        description: 'Billing, payments, and financial tracking',
        features: [
          { 
            key: 'monthly_payments', 
            label: 'Monthly Payments', 
            description: 'Recurring membership billing and payment tracking',
            routes: ['/app/payments']
          },
          { 
            key: 'one_time_payments', 
            label: 'One-Time Payments', 
            description: 'Track gear, tournaments, and special charges',
            routes: ['/app/one-time-payments']
          },
          { 
            key: 'family_discounts', 
            label: 'Family Discounts', 
            description: 'Multi-child discount management',
            routes: []
          },
        ]
      },
      communication: {
        name: 'Communication',
        icon: '💬',
        description: 'Messaging and communication tools',
        features: [
          { 
            key: 'internal_messaging', 
            label: 'Internal Messaging', 
            description: 'In-app messaging system between users',
            routes: ['/app/messages']
          },
          { 
            key: 'whatsapp_integration', 
            label: 'WhatsApp Integration', 
            description: 'WhatsApp group messaging and notifications',
            routes: []
          },
        ]
      },
      special: {
        name: 'Special Programs',
        icon: '✨',
        description: 'Events and special training programs',
        features: [
          { 
            key: 'special_classes', 
            label: 'Special Classes', 
            description: 'Manage events and special training sessions',
            routes: ['/app/special-classes', '/app/special-classes/parent']
          },
        ]
      },
      reports: {
        name: 'Reports & Analytics',
        icon: '📊',
        description: 'Data analysis and reporting tools',
        features: [
          { 
            key: 'reports_analytics', 
            label: 'Reports & Analytics', 
            description: 'General reporting features',
            routes: ['/app/reports']
          },
          { 
            key: 'tournament_selection', 
            label: 'Tournament Selection', 
            description: 'Generate tournament participant lists',
            routes: ['/app/tournament-selection']
          },
          { 
            key: 'contact_lists', 
            label: 'Contact Lists', 
            description: 'Export contact information',
            routes: ['/app/contact-list']
          },
          { 
            key: 'center_statistics', 
            label: 'Center Statistics', 
            description: 'View detailed center analytics',
            routes: ['/app/center-statistics']
          },
        ]
      },
      configuration: {
        name: 'Configuration',
        icon: '⚙️',
        description: 'System configuration and settings',
        features: [
          { 
            key: 'registration_tokens', 
            label: 'Registration Tokens', 
            description: 'Manage registration token system',
            routes: ['/app/token-management']
          },
        ]
      },
      ux_enhancements: {
        name: 'UX Enhancements',
        icon: '🎨',
        description: 'User experience and productivity features',
        features: [
          { 
            key: 'data_export', 
            label: 'Data Export', 
            description: 'Export data to CSV and Excel formats',
            routes: ['/app/students', '/app/payments', '/app/parents', '/app/classes', '/app/coaches', '/app/attendance-report', '/app/reports']
          },
          { 
            key: 'notification_preferences', 
            label: 'Notification Preferences', 
            description: 'Customize notification settings per user',
            routes: ['/app/notification-preferences', '/super-admin/notification-preferences']
          },
          { 
            key: 'saved_filters', 
            label: 'Saved Filters', 
            description: 'Save and reuse filter configurations',
            routes: ['/app/students', '/app/payments', '/app/parents', '/app/classes', '/app/coaches', '/app/attendance-report', '/app/reports']
          },
        ]
      }
    };
  }

  /**
   * Get all feature keys as a flat array
   * @returns {string[]} Array of all feature keys
   */
  static getAllFeatureKeys() {
    const categories = this.getFeatureCategories();
    const keys = [];
    
    Object.values(categories).forEach(category => {
      category.features.forEach(feature => {
        keys.push(feature.key);
      });
    });
    
    return keys;
  }

  /**
   * Get feature by key
   * @param {string} featureKey - Feature key to find
   * @returns {Object|null} Feature definition or null
   */
  static getFeatureByKey(featureKey) {
    const categories = this.getFeatureCategories();
    
    for (const category of Object.values(categories)) {
      const feature = category.features.find(f => f.key === featureKey);
      if (feature) {
        return { ...feature, category: category.name };
      }
    }
    
    return null;
  }
}

export default OrganizationFeatureService;
